import { Submittal } from '.'
import { NotificationEventItem } from '../NotificationEventItem'

/**
 * a common notification event item function for events where some approvals have
 * been marked
 *
 * @param options
 * @see {@link ApprovalOptions}
 * @see {@link partialApprovalDescription}
 */
export function PartialApprovalNotificationEventItem<
  ID extends Lowercase<string>,
  Name extends string
>({ id, name, description }: ApprovalOptions<ID, Name>) {
  return new NotificationEventItem({
    id,
    name,
    description: partialApprovalDescription(description),
  })
}

function partialApprovalDescription({
  group,
  object,
  approves = 'approves',
}: ApprovalOptions['description']) {
  return `Any User ${approves} a ${object} on behalf of their ${group}` as const
}

/**
 * a common notification event item function for events where all approvals have
 * been marked
 *
 * @param options
 * @see {@link AllApprovalsOptions}
 * @see {@link allApprovalsDescription}
 */
export function AllApprovalsNotificationEventItem<
  ID extends Lowercase<string>,
  Name extends string
>({ id, name, description }: AllApprovalsOptions<ID, Name>) {
  return new NotificationEventItem({
    id,
    name,
    description: allApprovalsDescription(description),
  })
}

type AllApprovalsOptions<
  ID extends Lowercase<string> = Lowercase<string>,
  Name extends string = string
> = ApprovalOptions<
  ID,
  Name,
  {
    /**
     * for some workflows, approving the last item will automatically advance the record's status
     *
     * use the name of the status
     *
     * @example
     * FieldDesignChangeStatus.Closed.name
     */
    sendsTo?: string
  }
>

function allApprovalsDescription({
  group,
  object,
  approves = 'approves',
  sendsTo,
}: AllApprovalsOptions['description']) {
  const start =
    `The last User ${approves} a ${object} on behalf of their ${group}` as const
  if (sendsTo) return `${start}, sending the ${object} to ${sendsTo}` as const
  return start
}

type ApprovalOptions<
  ID extends Lowercase<string> = Lowercase<string>,
  Name extends string = string,
  U = {}
> = Pick<NotificationEventItem<Name, ID>, 'name' | 'id'> & {
  description: U & {
    /**
     * objects which have some form of approval process
     *
     * @see {@link Submittal}
     */
    object: Submittal | 'Field Design Change'
    /**
     * the name of the group associated with the approval process
     */
    group: string
    /**
     * a custom verb if the action associated with the group is not "approves"
     *
     * @default 'approves'
     */
    approves?: string
  }
}
